import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  Cloud,
  CloudOff,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from "lucide-react";
import {
  accountSession,
  deleteCloudAccount,
  loginCloudAccount,
  logoutCloudAccount,
  registerCloudAccount,
  saveCloudData,
} from "@/components/account/cloudAccountApi";
import {
  clearAllLocalProfileData,
  clearCloudAccountCache,
  collectLocalProfileData,
  getDeviceId,
  hasMeaningfulLocalProfileData,
  hydrateLocalProfileData,
  loadCachedCloudAccount,
  loadCloudSyncState,
  localProfileBelongsToAccount,
  mergeProfileData,
  saveCachedCloudAccount,
  saveCloudSyncState,
  saveLocalProfileOwner,
} from "@/components/account/localProfileData";
import CompanionComingSoon from "@/pages/CompanionComingSoon";

const CloudAccountContext = createContext(null);
const SYNC_DEBOUNCE_MS = 1400;

const fieldClass =
  "mt-2 block w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm forr-text-ink focus:outline-none focus:ring-2 focus:ring-teal-500/30";

export function useCloudAccount() {
  const value = useContext(CloudAccountContext);
  if (!value) throw new Error("useCloudAccount must be used inside CloudAccountGate.");
  return value;
}

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const accountAlignedData = (value, account) => {
  const data = value && typeof value === "object" ? value : {};
  const email = normalizeEmail(account?.email);
  if (!email) return data;
  return {
    ...data,
    identity: data.identity
      ? { ...data.identity, email }
      : data.identity || null,
  };
};

export const AccountForm = ({
  cachedAccount,
  localData,
  onRegister,
  onLogin,
  busy,
  error,
  preferLogin = false,
  experience = "companion",
}) => {
  const existingEmail = localData.identity?.email || cachedAccount?.email || "";
  const [mode, setMode] = useState(preferLogin || cachedAccount ? "login" : "register");
  const [email, setEmail] = useState(existingEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [consent, setConsent] = useState(false);
  const passwordInputRef = useRef(null);
  const confirmPasswordInputRef = useRef(null);
  const hasExistingData = hasMeaningfulLocalProfileData(localData);
  const assessmentExperience = experience === "assessment";

  const togglePasswordVisibility = () => {
    // Some password managers populate the native field before React receives an
    // input event. Capture that value first so revealing it never clears autofill.
    const filledPassword = passwordInputRef.current?.value;
    const filledConfirmation = confirmPasswordInputRef.current?.value;
    if (typeof filledPassword === "string" && filledPassword !== password) {
      setPassword(filledPassword);
    }
    if (
      typeof filledConfirmation === "string" &&
      filledConfirmation !== confirmPassword
    ) {
      setConfirmPassword(filledConfirmation);
    }
    setPasswordVisible((visible) => !visible);
  };

  const submit = (event) => {
    event.preventDefault();
    if (mode === "register") {
      if (!consent || password !== confirmPassword) return;
      onRegister({ email: normalizeEmail(email), password });
      return;
    }
    onLogin({ email: normalizeEmail(email), password });
  };

  return (
    <div className="min-h-screen forr-canvas forr-text-ink">
      <section className="relative overflow-hidden px-5 py-12 sm:px-8 sm:py-20">
        <div className="absolute -top-48 left-1/2 h-[760px] w-[760px] -translate-x-1/2 forr-radial-teal opacity-60" />
        <div className="relative mx-auto max-w-3xl">
          <div className="inline-flex items-center gap-3">
            <span className="forr-hairline" />
            <span className="forr-overline">
              {assessmentExperience ? "Adaptive Assessment" : "APOS account"}
            </span>
          </div>
          <h1 className="mt-6 font-display text-4xl font-medium tracking-[-0.035em] forr-text-ink sm:text-6xl">
            {assessmentExperience
              ? "Understand how your nervous system responds to daily life."
              : "Keep your complete profile through every update."}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed forr-text-soft sm:text-lg">
            {assessmentExperience
              ? "The Adaptive Assessment turns your responses into a personalized explanation of how activation, recovery, sleep, anxiety, emotion, environment, and repeated behaviour may be interacting in your nervous system."
              : "Your assessments, check-ins, calendar, Companion history, responses, and progress are saved to your private cloud profile while remaining cached on this device for offline use."}
          </p>

          {assessmentExperience && (
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                ["Your nervous-system pattern", "See how your system may organize activation, energy, attention, emotion, action, and recovery."],
                ["Daily-life impact", "Understand how the pattern may connect with mental health, anxiety, sleep, stress, and behavioural urgency."],
                ["Strengths and current demands", "Identify what may already support you and what may be adding load or limiting recovery."],
                ["A practical starting direction", "Receive personalized priorities, a matched physiological direction, and clear ways to begin."],
              ].map(([title, body]) => (
                <div key={title} className="rounded-2xl p-5 forr-panel-information">
                  <div className="text-sm font-semibold forr-text-ink">{title}</div>
                  <p className="mt-2 text-xs leading-relaxed forr-text-soft">{body}</p>
                </div>
              ))}
            </div>
          )}

          {assessmentExperience && (
            <div className="mt-5 rounded-2xl p-5 forr-panel-action">
              <p className="text-sm leading-relaxed forr-text-ink">
                Your results are saved to your private APOS account so you can return to them and connect the same profile to APOS Companion when access opens. You may request deletion of your information at any time.
              </p>
            </div>
          )}

          <div
            className="mt-8 flex rounded-full p-1 forr-surface"
            style={{ border: "1px solid var(--forr-line-soft)" }}
          >
            {[
              ["register", hasExistingData ? "Protect this profile" : assessmentExperience ? "Create assessment account" : "Create account"],
              ["login", "Sign in"],
            ].map(([key, label]) => (
              <button
                type="button"
                key={key}
                onClick={() => {
                  setMode(key);
                  setPassword("");
                  setConfirmPassword("");
                  setPasswordVisible(false);
                  setConsent(false);
                }}
                className="flex-1 rounded-full px-4 py-3 text-sm font-medium"
                style={{
                  backgroundColor: mode === key ? "var(--forr-teal)" : "transparent",
                  color: mode === key ? "white" : "var(--forr-ink-soft)",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <form
            onSubmit={submit}
            className="mt-5 rounded-[28px] p-6 forr-surface sm:p-8"
            style={{ border: "1px solid var(--forr-line)" }}
          >
            <div className="flex items-start gap-4">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
                style={{ backgroundColor: "var(--forr-teal-tint)", color: "var(--forr-teal-deep)" }}
              >
                {mode === "register" ? <Cloud size={21} /> : <LockKeyhole size={21} />}
              </div>
              <div>
                <div className="forr-overline">
                  {mode === "register"
                    ? assessmentExperience
                      ? "Secure assessment profile"
                      : "Cloud profile"
                    : "Return to your profile"}
                </div>
                <h2 className="mt-1 font-display text-2xl forr-text-ink">
                  {mode === "register"
                    ? hasExistingData
                      ? "Save the profile already on this device"
                      : assessmentExperience
                        ? "Create your account to begin"
                        : "Create your APOS account"
                    : "Sign in without repeating the assessment"}
                </h2>
              </div>
            </div>

            {mode === "register" && hasExistingData && (
              <div className="mt-6 rounded-2xl p-4 forr-panel-information">
                <div className="text-sm font-medium forr-text-ink">Existing data found</div>
                <p className="mt-2 text-xs leading-relaxed forr-text-soft">
                  APOS will import the identity, every locally saved assessment, current check-ins, calendar events, routines, and Companion conversation on this device into the new account.
                </p>
              </div>
            )}

            <div className="mt-7 grid gap-5">
              <label className="text-sm forr-text-soft">
                Email
                <span className="relative block">
                  <Mail size={16} className="pointer-events-none absolute left-4 top-[22px] forr-text-mute" />
                  <input
                    required
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className={`${fieldClass} pl-11`}
                  />
                </span>
              </label>
              <div>
                <div className="flex items-center justify-between gap-4">
                  <label htmlFor="apos-account-password" className="text-sm forr-text-soft">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    aria-controls={
                      mode === "register"
                        ? "apos-account-password apos-account-confirm-password"
                        : "apos-account-password"
                    }
                    aria-pressed={passwordVisible}
                    className="inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold forr-text-teal-deep hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  >
                    {passwordVisible ? (
                      <EyeOff size={15} aria-hidden="true" />
                    ) : (
                      <Eye size={15} aria-hidden="true" />
                    )}
                    {passwordVisible ? "Hide password" : "Show password"}
                  </button>
                </div>
                <input
                  id="apos-account-password"
                  ref={passwordInputRef}
                  required
                  type={passwordVisible ? "text" : "password"}
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  minLength="12"
                  maxLength="128"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={fieldClass}
                />
                <span className="mt-2 block text-xs forr-text-mute">
                  {assessmentExperience
                    ? "Use at least 12 characters and store it securely."
                    : "Use at least 12 characters. Password recovery is not automated during this beta, so store it securely."}
                </span>
              </div>
              {mode === "register" && (
                <div>
                  <label
                    htmlFor="apos-account-confirm-password"
                    className="text-sm forr-text-soft"
                  >
                    Confirm password
                  </label>
                  <input
                    id="apos-account-confirm-password"
                    ref={confirmPasswordInputRef}
                    required
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="new-password"
                    minLength="12"
                    maxLength="128"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className={fieldClass}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <span className="mt-2 block text-xs text-red-700">Passwords do not match.</span>
                  )}
                </div>
              )}
            </div>

            {mode === "register" && (
              <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl p-4 forr-surface-tint">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm leading-relaxed forr-text-soft">
                  I agree to save my APOS identity, assessment history, state, calendar, participation, and Companion data to my private account so it remains available after updates or on another device. Authorized APOS administrators may review my profile, assessments, check-ins, calendar, and participation data for account support and product improvement. Companion message text is not shown in the participant directory.
                </span>
              </label>
            )}

            {error && (
              <p className="mt-5 rounded-xl bg-red-50 p-3 text-sm text-red-800" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={
                busy ||
                !email ||
                password.length < 12 ||
                (mode === "register" && (!consent || password !== confirmPassword))
              }
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold forr-btn-primary disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
            >
              {busy && <LoaderCircle size={17} className="animate-spin" />}
              {mode === "register"
                ? assessmentExperience
                  ? "Create account and begin"
                  : "Create and sync account"
                : "Sign in and restore profile"}
            </button>

            <div className="mt-6 flex items-start gap-2 text-xs leading-relaxed forr-text-mute">
              <ShieldCheck size={15} className="mt-0.5 shrink-0" />
              Your password is not sent to Adaptive Companion AI. Email is used for account access and is excluded from ordinary AI conversation context.
            </div>
          </form>

        </div>
      </section>
    </div>
  );
};

const LoadingAccount = () => (
  <div className="flex min-h-screen items-center justify-center forr-canvas">
    <div className="text-center">
      <LoaderCircle size={28} className="mx-auto animate-spin forr-text-teal-deep" />
      <div className="mt-4 font-display text-xl forr-text-ink">Opening your APOS profile</div>
      <p className="mt-2 text-sm forr-text-mute">Checking saved cloud data and this device.</p>
    </div>
  </div>
);

const CloudAccountGate = ({ children }) => {
  const location = useLocation();
  const normalizedPath = location.pathname === "/"
    ? "/"
    : location.pathname.replace(/\/+$/, "");
  const betaSignInRequested =
    normalizedPath === "/app" &&
    new URLSearchParams(location.search).get("beta") === "1";
  const cachedAccount = useMemo(() => loadCachedCloudAccount(), []);
  const initialSyncState = useMemo(() => loadCloudSyncState(), []);
  const [status, setStatus] = useState("checking");
  const [account, setAccount] = useState(cachedAccount);
  const [revision, setRevision] = useState(Number(initialSyncState.revision) || 0);
  const [syncStatus, setSyncStatus] = useState(initialSyncState.status || "checking");
  const [lastSyncedAt, setLastSyncedAt] = useState(initialSyncState.lastSyncedAt || null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const accountRef = useRef(cachedAccount);
  const revisionRef = useRef(Number(initialSyncState.revision) || 0);
  const lastSyncedRef = useRef(initialSyncState.lastSyncedAt || null);
  const syncTimerRef = useRef(null);
  const hydratingRef = useRef(false);
  const syncingRef = useRef(false);
  const startupRef = useRef(false);

  const setAccountValue = useCallback((value) => {
    const next = value ? saveCachedCloudAccount(value) : null;
    accountRef.current = next;
    setAccount(next);
    return next;
  }, []);

  const updateRevision = useCallback((value, nextStatus = "synced") => {
    const nextRevision = Number(value) || 0;
    const syncedAt =
      nextStatus === "synced" ? new Date().toISOString() : lastSyncedRef.current;
    revisionRef.current = nextRevision;
    lastSyncedRef.current = syncedAt;
    setRevision(nextRevision);
    setSyncStatus(nextStatus);
    setLastSyncedAt(syncedAt);
    saveCloudSyncState({
      revision: nextRevision,
      lastSyncedAt: syncedAt,
      status: nextStatus,
    });
  }, []);

  const applySnapshot = useCallback(
    (snapshot, accountValue, { mergeLocal = false } = {}) => {
      hydratingRef.current = true;
      const local = collectLocalProfileData();
      const shouldMerge = mergeLocal || localProfileBelongsToAccount(accountValue);
      const selected = shouldMerge
        ? mergeProfileData(snapshot?.data || null, local)
        : snapshot?.data || {};
      const aligned = accountAlignedData(selected, accountValue);
      hydrateLocalProfileData(aligned, { replace: !shouldMerge });
      saveLocalProfileOwner(accountValue);
      hydratingRef.current = false;
      if (accountValue) setAccountValue(accountValue);
      updateRevision(snapshot?.revision || 0, "synced");
      return aligned;
    },
    [setAccountValue, updateRevision],
  );

  const syncNow = useCallback(async () => {
    if (syncingRef.current || !accountRef.current) return null;
    syncingRef.current = true;
    setSyncStatus("syncing");
    let data = collectLocalProfileData();
    let expectedRevision = revisionRef.current;

    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const result = await saveCloudData({
            data,
            expectedRevision,
            deviceId: getDeviceId(),
          });
          if (result.snapshot) {
            applySnapshot(result.snapshot, accountRef.current, { mergeLocal: true });
            setStatus("authenticated");
            return result.snapshot;
          }
          return null;
        } catch (requestError) {
          if (
            requestError.status === 409 &&
            requestError.body?.snapshot &&
            attempt === 0
          ) {
            data = mergeProfileData(requestError.body.snapshot.data, data);
            hydrateLocalProfileData(data);
            expectedRevision = Number(requestError.body.snapshot.revision) || 0;
            revisionRef.current = expectedRevision;
            continue;
          }
          throw requestError;
        }
      }
      return null;
    } catch (requestError) {
      if (requestError.status === 401) {
        setStatus("unauthenticated");
        setAccountValue(null);
        clearCloudAccountCache({ preserveOwner: true });
      } else {
        setStatus("offline");
        updateRevision(revisionRef.current, "offline");
      }
      return null;
    } finally {
      syncingRef.current = false;
    }
  }, [applySnapshot, setAccountValue, updateRevision]);

  const scheduleSync = useCallback(() => {
    if (hydratingRef.current || !accountRef.current) return;
    if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(syncNow, SYNC_DEBOUNCE_MS);
  }, [syncNow]);

  useEffect(() => {
    if (startupRef.current) return undefined;
    startupRef.current = true;
    let cancelled = false;

    const start = async () => {
      try {
        const result = await accountSession();
        if (cancelled) return;
        const merged = applySnapshot(result.snapshot, result.account);
        setStatus("authenticated");
        setError("");
        if (JSON.stringify(merged) !== JSON.stringify(result.snapshot?.data || {})) {
          window.setTimeout(syncNow, 0);
        }
      } catch (requestError) {
        if (cancelled) return;
        if (requestError.status === 401) {
          setStatus("unauthenticated");
          setAccountValue(null);
          clearCloudAccountCache({ preserveOwner: true });
        } else if (
          cachedAccount &&
          localProfileBelongsToAccount(cachedAccount) &&
          hasMeaningfulLocalProfileData()
        ) {
          setStatus("offline");
          updateRevision(revisionRef.current, "offline");
        } else {
          setStatus("unauthenticated");
          setError("Cloud storage could not be reached. Refresh when the connection is available.");
        }
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [applySnapshot, cachedAccount, setAccountValue, syncNow, updateRevision]);

  useEffect(() => {
    if (!account) return undefined;
    const changed = () => scheduleSync();
    const online = () => {
      setStatus("authenticated");
      syncNow();
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") syncNow();
    };
    const events = [
      "apos-local-data-changed",
      "adaptive-profile-saved",
      "adaptive-participant-identity-saved",
      "adaptive-calendar-updated",
      "companion-routine-settings-saved",
      "adaptive-companion-conversation-saved",
      "adaptive-active-assessment-changed",
    ];
    events.forEach((name) => window.addEventListener(name, changed));
    window.addEventListener("storage", changed);
    window.addEventListener("online", online);
    document.addEventListener("visibilitychange", visibility);
    const interval = window.setInterval(syncNow, 60_000);

    return () => {
      events.forEach((name) => window.removeEventListener(name, changed));
      window.removeEventListener("storage", changed);
      window.removeEventListener("online", online);
      document.removeEventListener("visibilitychange", visibility);
      window.clearInterval(interval);
      if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    };
  }, [account, scheduleSync, syncNow]);

  const register = async ({ email, password }) => {
    setBusy(true);
    setError("");
    try {
      const local = collectLocalProfileData();
      const consentAt = new Date().toISOString();
      const initialData = accountAlignedData(
        {
          ...local,
          metadata: {
            ...(local.metadata || {}),
            adminReviewConsentVersion: 1,
            adminReviewConsentAt: consentAt,
          },
        },
        { email },
      );
      const result = await registerCloudAccount({
        email,
        password,
        deviceId: getDeviceId(),
        initialData,
      });
      applySnapshot(result.snapshot, result.account, { mergeLocal: true });
      setStatus("authenticated");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const login = async ({ email, password }) => {
    setBusy(true);
    setError("");
    try {
      const result = await loginCloudAccount({
        email,
        password,
        deviceId: getDeviceId(),
      });
      const sameOwner = localProfileBelongsToAccount(result.account);
      const merged = applySnapshot(result.snapshot, result.account, {
        mergeLocal: sameOwner,
      });
      setStatus("authenticated");
      if (
        sameOwner &&
        JSON.stringify(merged) !== JSON.stringify(result.snapshot?.data || {})
      ) {
        window.setTimeout(syncNow, 0);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const signOut = useCallback(async () => {
    const snapshot = await syncNow();
    if (!snapshot) {
      setSyncStatus("offline");
      return { ok: false, error: "Connect to the internet and sync before signing out." };
    }
    await logoutCloudAccount();
    clearAllLocalProfileData();
    clearCloudAccountCache({ preserveOwner: false });
    setAccountValue(null);
    revisionRef.current = 0;
    setRevision(0);
    setLastSyncedAt(null);
    setStatus("unauthenticated");
    setSyncStatus("signed-out");
    return { ok: true };
  }, [setAccountValue, syncNow]);

  const removeAccount = useCallback(
    async (password) => {
      const result = await deleteCloudAccount(password);
      clearAllLocalProfileData({ preserveDeviceId: false });
      clearCloudAccountCache({ preserveOwner: false });
      setAccountValue(null);
      revisionRef.current = 0;
      setRevision(0);
      setLastSyncedAt(null);
      setStatus("unauthenticated");
      setSyncStatus("deleted");
      return result;
    },
    [setAccountValue],
  );

  const value = useMemo(
    () => ({
      status,
      account,
      revision,
      syncStatus,
      lastSyncedAt,
      syncNow,
      scheduleSync,
      signOut,
      deleteAccount: removeAccount,
      isOffline: status === "offline",
    }),
    [
      account,
      lastSyncedAt,
      removeAccount,
      revision,
      scheduleSync,
      signOut,
      status,
      syncNow,
      syncStatus,
    ],
  );

  if (status === "checking") return <LoadingAccount />;
  if (status === "unauthenticated") {
      if (normalizedPath === "/app" && !betaSignInRequested) {
        return <CompanionComingSoon />;
      }
    return (
      <AccountForm
        cachedAccount={cachedAccount}
        localData={collectLocalProfileData()}
        onRegister={register}
        onLogin={login}
        busy={busy}
        error={error}
        preferLogin={normalizedPath.startsWith("/admin")}
        experience={normalizedPath === "/assessment/start" ? "assessment" : "companion"}
      />
    );
  }

  return (
    <CloudAccountContext.Provider value={value}>
      {status === "offline" && (
        <div className="fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-2 bg-amber-100 px-4 py-2 text-xs text-amber-900">
          <CloudOff size={14} /> Offline cache in use. Changes will sync when the connection returns.
        </div>
      )}
      {children}
    </CloudAccountContext.Provider>
  );
};

export default CloudAccountGate;
